import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DEEP_GREEN, MUTED, WARN_ORANGE } from '../colorTokens';
import { OUTCOME_COLORS, formatDay, formatNumber, formatPercent } from './format';
import { deletePlan, uploadPlan, usePlanComparison } from './useReleaseCadence';
import { Caveat, Headline, MeasureRow, ScoreCard, TrendLine } from './ScoreParts';

const SEGMENTS = [
  { key: 'onTime', label: 'On time', color: '#4caf50' },
  { key: 'early', label: 'Early', color: '#81c784' },
  { key: 'late', label: 'Late', color: '#ff9800' },
  { key: 'failed', label: 'Failed, not recovered', color: OUTCOME_COLORS.failed },
  { key: 'skipped', label: 'Skipped', color: OUTCOME_COLORS.skipped },
  { key: 'notRun', label: 'Not run', color: '#7a7a7a' },
  { key: 'noData', label: 'No history', color: '#e0e0e0' }
];

const COVERAGE = {
  full: null,
  partial: 'Run history starts after this weekend began, so "Not run" may include instances upgraded earlier.',
  none: 'This weekend is older than the recorded run history, so no outcomes can be shown.'
};

const PlanBar = ({ wave }) => (
  <Tooltip placement="top" title={SEGMENTS.map((s) => `${s.label}: ${formatNumber(wave[s.key])}`).join(' · ')}>
    <Box sx={{ display: 'flex', height: 14, width: 260, borderRadius: '7px', overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.06)' }}>
      {SEGMENTS.map((s) => (wave[s.key] ? <Box key={s.key} sx={{ width: `${(wave[s.key] / wave.planned) * 100}%`, bgcolor: s.color }} /> : null))}
    </Box>
  </Tooltip>
);

const QualityNotes = ({ quality, warnings }) => {
  const notes = [
    ...warnings,
    quality.provisioningScheduled ? `${quality.provisioningScheduled} instances are still "provisioning" but scheduled for upgrade.` : null,
    quality.duplicateRows ? `${quality.duplicateRows} duplicate rows were ignored.` : null,
    quality.repeatedHeaders ? `${quality.repeatedHeaders} repeated header rows were skipped inside the sheets.` : null,
    quality.skippedRows ? `${quality.skippedRows} rows without an env_name or instance were skipped.` : null
  ].filter(Boolean);
  if (!notes.length) return null;
  return (
    <Alert severity="info" sx={{ borderRadius: '10px' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Spreadsheet data quality</Typography>
      {notes.map((n) => <Typography key={n} variant="body2">{n}</Typography>)}
    </Alert>
  );
};

const weekendLabel = (iso) => formatDay(iso);

const planTip = (p) => ({ title: `Weekend of ${p.label}`, lines: [`${formatPercent(p.completionRate)} done as planned`, `${formatNumber(p.planned)} instances planned · ${p.version}`] });

const PlanScorecard = ({ scorecard }) => {
  if (!scorecard) return null;
  const { latest, baseline, measures, trend, excluded } = scorecard;
  if (!latest) {
    return <Caveat>No weekend is fully covered by the recorded run history yet, so delivery against plan cannot be scored.</Caveat>;
  }
  const completion = measures.find((m) => m.key === 'completionRate');
  const points = trend.map((t) => ({ ...t, label: weekendLabel(t.dateStart) }));
  const since = baseline && `since the first ${baseline.sheetNames.length > 1 ? `${baseline.sheetNames.length} ` : ''}fully recorded weekend${baseline.sheetNames.length > 1 ? 's' : ''}, which averaged ${formatPercent(completion.earlier)}.`;
  return (
    <ScoreCard
      headline={<Headline caption={`Latest fully recorded weekend · ${formatDay(latest.dateStart)} – ${formatDay(latest.dateEnd)} · ${latest.version}`} value={formatPercent(completion.value)} label="of planned instances upgraded" measure={completion} since={since} />}
      chart={points.length > 1 ? <TrendLine title="Done as planned, weekend by weekend" data={points} valueKey="completionRate" unit="rate" xKey="label" tip={planTip} /> : null}
    >
      <MeasureRow measures={measures} skip={['completionRate']} baselineLabel={baseline && 'average of the earlier weekends'} />
      <Caveat>
        {formatNumber(latest.planned)} instances planned: {formatNumber(latest.onTime + latest.early)} on time or early, {formatNumber(latest.late)} late, {formatNumber(latest.failed)} failed, {formatNumber(latest.skipped)} skipped, {formatNumber(latest.notRun)} not run.
        {!baseline && ' Improvement appears once a second weekend is fully recorded.'}
        {excluded > 0 && ` ${excluded} earlier weekend${excluded > 1 ? 's are' : ' is'} left out because the run history does not fully cover ${excluded > 1 ? 'them' : 'it'}.`}
      </Caveat>
    </ScoreCard>
  );
};

const PlanVsActualView = ({ overview, onChanged }) => {
  const plans = overview.plans;
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const inputRef = useRef(null);
  const planId = plans.some((p) => p.id === selected) ? selected : (plans[0] && plans[0].id) || '';
  const { data, loading, error } = usePlanComparison(planId);

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await uploadPlan(file);
      setSelected(result.id);
      setMessage({ severity: 'success', text: `Loaded ${result.waves} weekends and ${formatNumber(result.instances)} planned instances.` });
      await onChanged();
    } catch (err) {
      setMessage({ severity: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!planId || !window.confirm('Delete this uploaded plan? Recorded deployment runs are not affected.')) return;
    setBusy(true);
    try {
      await deletePlan(planId);
      setSelected('');
      await onChanged();
    } catch (err) {
      setMessage({ severity: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {plans.length > 0 && (
          <Select size="small" value={planId} onChange={(e) => setSelected(e.target.value)} sx={{ minWidth: 260, borderRadius: '10px' }}>
            {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
        )}
        <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFile} />
        <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <UploadFileIcon />} disabled={busy} onClick={() => inputRef.current && inputRef.current.click()} sx={{ textTransform: 'none', borderRadius: '10px' }}>
          Upload deployment schedule (.xlsx)
        </Button>
        {planId && (
          <Tooltip title="Delete this plan">
            <span><IconButton size="small" onClick={handleDelete} disabled={busy}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        )}
      </Box>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {!plans.length && (
        <Typography sx={{ color: MUTED }}>
          Upload a deployment schedule workbook (one "Week_…" sheet per rollout weekend) to compare what was planned with what Datadog recorded.
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <CircularProgress size={22} />}

      {data && (
        <>
          <PlanScorecard scorecard={data.scorecard} />
          <QualityNotes quality={data.quality} warnings={data.warnings} />
          <Paper variant="outlined" sx={{ borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8F9FA', '& th': { color: DEEP_GREEN, fontWeight: 600, borderBottom: '2px solid rgba(26,46,34,0.15)' } }}>
                  <TableCell>Weekend</TableCell>
                  <TableCell>Plan version</TableCell>
                  <TableCell align="right">Planned</TableCell>
                  <TableCell>Actual against plan</TableCell>
                  <TableCell align="right">Completed</TableCell>
                  <TableCell align="right">Late</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Not run</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.waves.map((w) => (
                  <TableRow key={w.sheetName} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {formatDay(w.dateStart)} – {formatDay(w.dateEnd)}
                      {COVERAGE[w.coverage] && (
                        <Tooltip title={COVERAGE[w.coverage]}>
                          <Chip size="small" label={w.coverage === 'none' ? 'No history' : 'Partial history'} sx={{ ml: 1, height: 20, borderRadius: '8px' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{w.version}</TableCell>
                    <TableCell align="right">{formatNumber(w.planned)}</TableCell>
                    <TableCell><PlanBar wave={w} /></TableCell>
                    <TableCell align="right">{formatPercent(w.completionRate)}</TableCell>
                    <TableCell align="right" sx={{ color: w.late ? WARN_ORANGE : 'inherit' }}>{formatNumber(w.late)}</TableCell>
                    <TableCell align="right">{formatNumber(w.failed)}</TableCell>
                    <TableCell align="right">{w.coverage === 'none' ? '–' : formatNumber(w.notRun)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {SEGMENTS.map((s) => (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: s.color }} />
                <Typography variant="caption" sx={{ color: MUTED }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
          {data.unplanned.some((u) => u.instances > 0) && (
            <Typography variant="body2" sx={{ color: MUTED }}>
              Upgraded but not in the plan: {data.unplanned.filter((u) => u.instances > 0).map((u) => `${formatNumber(u.instances)} instances on ${u.baseVersion}`).join(', ')}.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default PlanVsActualView;
